import React from "react";
import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";

interface ShortTermRentalMonitoringProps {
  title: string;
  introText: string;
  identificationAndComplianceText: string;
  firstYearExampleText: string;
  adaptabilityText: string;
  enforcementText: string;
  problemTitle: string;
  problemDescription: string;
  solutionTitle: string;
  solutionDescription: string;
}

interface InformationSectionProps extends ShortTermRentalMonitoringProps {
  textPosition?: "left" | "right";
  imageSrc?: string;
  imageAlt?: string;
}

const InformationSection: React.FC<InformationSectionProps> = ({
  title,
  introText,
  identificationAndComplianceText,
  firstYearExampleText,
  adaptabilityText,
  enforcementText,
  problemTitle,
  problemDescription,
  solutionTitle,
  solutionDescription,
  textPosition = "left",
  imageSrc,
  imageAlt = "Information Section Image",
}) => {
  return (
    <Box sx={{ width: "100%", bgcolor: "#0b0f17" }}>
      <Grid
        container
        spacing={4}
        sx={{
          py: { xs: 4, md: 8 },
          px: { xs: 2, md: 4 },
          maxWidth: 1280,
          mx: "auto",
        }}
      >
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            order: textPosition === "left" ? 1 : 2,
          }}
        >
          <Box sx={{ marginBottom: "2rem" }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
              }}
            >
              {title}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#cbd5e1" }}>
              {introText}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#94a3b8" }}>
              {identificationAndComplianceText}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#94a3b8" }}>
              {firstYearExampleText}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#94a3b8" }}>
              {adaptabilityText}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#94a3b8" }}>
              {enforcementText}
            </Typography>
          </Box>

          <Box sx={{ marginBottom: "2rem" }}>
            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              sx={{ color: "#a78bfa", fontWeight: 700 }}
            >
              {problemTitle}
            </Typography>
            <Typography variant="body1" sx={{ color: "#94a3b8" }}>
              {problemDescription}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              sx={{ color: "#a78bfa", fontWeight: 700 }}
            >
              {solutionTitle}
            </Typography>
            <Typography variant="body1" sx={{ color: "#94a3b8" }}>
              {solutionDescription}
            </Typography>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            order: textPosition === "left" ? 2 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          {imageSrc ? (
            <Box
              component="img"
              src={imageSrc}
              alt={imageAlt}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                borderRadius: "16px",
                border: "1px solid rgba(51, 65, 85, 0.8)",
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No Image Provided
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default InformationSection;
